import { Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, switchMap, first } from 'rxjs/operators';
import { MangoWallet, UserAccountEmail, UserBankingInfo, UserProfile } from '../../models';

@Injectable({
  providedIn: 'root',
})
export class AdminUserService {
  private http = inject(HttpClient);

  configUrl = environment.apiUrl;

  public getMoreBlockedEmail = new Subject<{limit: number, offset: number, searchQuery: string}>();

  updateUserInfo(user: UserProfile): Observable<UserProfile> {
    const route = `${this.configUrl}a/users`;
    return this.http.put<UserProfile>(route, user).pipe(
      first(),
    );
  }

  // this route update the banking infos from mangoay, saves the new values then respond with it
  getUserBankingInfo(userInfoId): Observable<UserBankingInfo> {
    const route = `${this.configUrl}a/userBankingInfo/${userInfoId}`;
    return this.http.get<UserBankingInfo>(route)
      .pipe(
        first(),
      );
  }

  // user banking info history
  getUserBankingInfoHistory(userInfoId) {
    const route = `${this.configUrl}a/userBankingInfoHistory/${userInfoId}`;
    return this.http.get<UserBankingInfo[]>(route)
      .pipe(
        first(),
      );
  }

  // user banking info history
  getUserAccountEmails(userAccountId) {
    const route = `${this.configUrl}a/userAccountEmails/${userAccountId}`;
    return this.http.get<UserAccountEmail[]>(route)
      .pipe(
        first(),
      );
  }

  toggleUserBankingInfo(userBankingId) {
    const route = `${this.configUrl}a/userBankingInfo/toggle/${userBankingId}`;
    return this.http.put<UserBankingInfo>(route, {})
      .pipe(
        first(),
      );
  }

  deleteUser(userInfoId) {
    const route = `${this.configUrl}a/users/${userInfoId}`;
    return this.http.delete(route);
  }

  disconnectUser(userInfoId) {
    const route = `${this.configUrl}a/users/auth/${userInfoId}`;
    return this.http.delete(route);
  }

  unlockUser(userAccountId) {
    const route = `${this.configUrl}a/userAccount/unlock/${userAccountId}`;
    return this.http.put(route, {});
  }

  unlockPayins(userInfoId) {
    const route = `${this.configUrl}a/userAccount/unlockPayins/${userInfoId}`;
    return this.http.put(route, {});
  }

  getBlockedEmails() {
    const route = `${this.configUrl}a/u/blockedEmails`;
    return this.getMoreBlockedEmail.pipe(
      // wait 300ms after each keystroke before considering the term
      debounceTime(300),
      // switch to new search observable each time the term changes
      switchMap( ({limit, offset, searchQuery}) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery.toString()),
          },
        });
        return this.http.get<{
          rows: Array<{
            email: string,
            count: number,
            reason: string,
          }>,
          count: number
        }>(route, {params});
      }),
    );
  }

  addBlockedEmail(email: string, reason: string) {
    const route = `${this.configUrl}a/u/blockedEmails`;
    return this.http.post(route, {email, reason})
      .pipe(
        first(),
      );
  }

  reactivateEmail(blockedEmail: string) {
    const route = `${this.configUrl}a/u/blockedEmails/${encodeURIComponent(blockedEmail)}`;
    return this.http.delete(route)
      .pipe(
        first(),
      );
  }

  getUserTotal(dateStart: Date, dateEnd?: Date) {
    let route = `${this.configUrl}a/u/user-total?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{totalUser: number}>(route).pipe();
  }

  getKYCStats() {
    const route = `${this.configUrl}a/u/kyc-stats`;
    return this.http.get<{kycTotal: number}>(route).pipe();
  }

  getWaitlistTotal(dateStart: Date, dateEnd?: Date) {
    let route = `${this.configUrl}a/u/waitlist-total?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{totalWaitlist: number}>(route).pipe();
  }

  getSellerTotal(dateStart: Date, dateEnd?: Date) {
    let route = `${this.configUrl}a/u/seller-total?dateStart=${dateStart.toISOString()}`;
    if (dateEnd) {
      route += `&dateEnd=${dateEnd.toISOString()}`;
    }
    return this.http.get<{ totalSellers: number; totalSoldSellers: number }>(route).pipe();
  }

  addMangopayWallet(currency: string, user: UserProfile) {
    const route = `${this.configUrl}w/a/mangopay/addMangopayWallet`;
    return this.http.post<MangoWallet>(route, {currency, user}).pipe(
      first(),
    );
  }

}
